import { useState, useEffect } from "react";
import { Copy, Check, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { pushProjectShareLinkCopied } from "@/lib/analytics";

interface ShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: string;
}

interface Share {
  id: string;
  token: string;
  password_hash: string | null;
  first_copied_at: string | null;
}

export const ShareModal = ({
  open,
  onOpenChange,
  projectId,
}: ShareModalProps) => {
  const [copied, setCopied] = useState(false);
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [share, setShare] = useState<Share | null>(null);
  const { toast } = useToast();

  // Fetch or create share when modal opens
  useEffect(() => {
    if (open && projectId) {
      fetchOrCreateShare();
    }
  }, [open, projectId]);

  const fetchOrCreateShare = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    try {
      // Try to fetch existing share
      const { data: existingShare, error: fetchError } = await supabase
        .from("shares")
        .select("id, token, password_hash, first_copied_at")
        .eq("project_id", projectId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existingShare) {
        setShare(existingShare);
        setPasswordEnabled(!!existingShare.password_hash);
      } else {
        // Create new share
        const { data: newShare, error: insertError } = await supabase
          .from("shares")
          .insert({ project_id: projectId })
          .select("id, token, password_hash, first_copied_at")
          .single();

        if (insertError) throw insertError;
        setShare(newShare);
        setPasswordEnabled(false);
      }
    } catch (error) {
      console.error("Error fetching/creating share:", error);
      toast({
        title: "Error",
        description: "Failed to load share settings.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePassword = async (enabled: boolean) => {
    if (!share) return;
    
    setPasswordEnabled(enabled);
    
    if (!enabled) {
      // Clear the password
      setIsSaving(true);
      try {
        const { error } = await supabase.functions.invoke("hash-password", {
          body: { share_id: share.id, clear: true },
        });

        if (error) throw error;

        setShare({ ...share, password_hash: null });
        setPassword("");
        toast({
          title: "Password removed",
          description: "This link is no longer password protected.",
        });
      } catch (error) {
        console.error("Error clearing password:", error);
        toast({
          title: "Error",
          description: "Failed to remove password protection.",
          variant: "destructive",
        });
        setPasswordEnabled(true);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleSavePassword = async () => {
    if (!share || !password.trim()) {
      toast({
        title: "Password required",
        description: "Please enter a password to protect this link.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase.functions.invoke("hash-password", {
        body: { share_id: share.id, password: password.trim() },
      });

      if (error) throw error;

      setShare({ ...share, password_hash: "set" });
      setPassword("");
      toast({
        title: "Password saved",
        description: "This link is now password protected.",
      });
    } catch (error) {
      console.error("Error saving password:", error);
      toast({
        title: "Error",
        description: "Failed to save password.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const shareUrl = share 
    ? `${window.location.origin}/review/${share.token}`
    : "";

  const handleCopy = async () => {
    if (!shareUrl || !share || !projectId) return;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      
      // Determine if this is the first copy
      const isFirstCopy = !share.first_copied_at;
      
      // Fire analytics event
      pushProjectShareLinkCopied({
        project_id: projectId,
        is_first_share_copy: isFirstCopy,
      });
      
      // Update first_copied_at if this is the first copy
      if (isFirstCopy) {
        const { error } = await supabase
          .from("shares")
          .update({ first_copied_at: new Date().toISOString() })
          .eq("id", share.id);
        
        if (!error) {
          setShare({ ...share, first_copied_at: new Date().toISOString() });
        }
      }
      
      toast({
        title: "Copied to clipboard",
        description: "Share link has been copied successfully.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try copying manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Share Project</DialogTitle>
          <DialogDescription>
            Share this link with stakeholders to let them view and comment on this email.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* Share URL Display */}
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-xl border border-foreground/10 bg-muted/50 px-4 py-3">
                <p className="font-mono text-sm text-foreground break-all">
                  {shareUrl || "Loading..."}
                </p>
              </div>
              <Button
                onClick={handleCopy}
                variant="default"
                className="shrink-0 bg-foreground text-primary-foreground hover:bg-accent hover:text-accent-foreground"
                disabled={!shareUrl}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>

            {/* Password Protection Section */}
            <div className="border-t border-foreground/10 pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  <Label htmlFor="password-toggle" className="cursor-pointer">
                    Password protect this link
                  </Label>
                </div>
                <Switch
                  id="password-toggle"
                  checked={passwordEnabled}
                  onCheckedChange={handleTogglePassword}
                  disabled={isSaving}
                />
              </div>

              {passwordEnabled && (
                <div className="mt-3 space-y-3">
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={share?.password_hash ? "Enter new password" : "Enter password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  <Button
                    onClick={handleSavePassword}
                    variant="outline"
                    disabled={!password.trim() || isSaving}
                    className="w-full hover:bg-accent hover:text-accent-foreground"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {share?.password_hash ? "Update Password" : "Save Password"}
                  </Button>
                </div>
              )}
            </div>

            {/* Helper Text */}
            <p className="text-sm text-muted-foreground">
              {share?.password_hash
                ? "Only people with the link and password can view and comment."
                : passwordEnabled && password
                ? "Click 'Save Password' to protect this link."
                : "Anyone with this link can view the email and leave comments."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
